import TicketLink from './TicketLink';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import TimeSheet from './TimeSheet';
import { downloadCsv, downloadXlsx } from '../lib/exportTable';
import './ContractorWorkOrders.css';
import { hours } from '../lib/hours';
import { ListTools, StatusBadge } from './ListTools';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Shared, because three local copies is how one of them came to render
// ten hours as 1h. See lib/hours.js.
const hrs = hours;

/**
 * A contractor's own work, and the time they have put into it.
 *
 * Time is logged in sessions rather than as one final figure: two hours on
 * Tuesday and three on Thursday are two entries against the same job, and the
 * work order carries the running total. That is how the hours_logs table was
 * always shaped - what was missing is that both endpoints were admin-only, so
 * the one person who knows the hours could not record them.
 */
export default function ContractorWorkOrders({ contractorId, token }) {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Sort and narrow. Gordon: the Work & time tab had no status on a job and
  // no way to sort or filter the jobs. The ticket's status rides on each
  // work order now (routes/work-orders.js SELECT), and these three work on
  // what is already loaded.
  const [sortBy, setSortBy] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/my-work-orders`, auth);
      setOrders(res.data?.workOrders || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your work orders');
    } finally {
      setLoading(false);
    }
    // auth is derived from token and would otherwise change identity each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /**
   * The columns of the timesheet download.
   *
   * One row per session rather than one per job, because that is what the page
   * is for and what anybody checking an invoice needs - a total of 6.5 hours
   * tells you nothing about which days it was worked on. The job's own figures
   * ride along on each row so the file still answers both questions.
   */
  const TIME_COLUMNS = [
    { key: 'date_worked',       label: 'Date',        width: 12, type: 'date' },
    { key: 'hours_logged',      label: 'Hours',       width: 10, type: 'number' },
    { key: 'ticket_number',     label: 'Ticket',      width: 14 },
    { key: 'ticket_title',      label: 'Job',         width: 40 },
    { key: 'work_order_number', label: 'Work order',  width: 16 },
    { key: 'description',       label: 'What you did', width: 40 },
    { key: 'job_logged',        label: 'Job total',   width: 12, type: 'number' },
    { key: 'job_allocated',     label: 'Job allocated', width: 13, type: 'number' },
    { key: 'work_order_status', label: 'Status',      width: 13 }
  ];

  /**
   * Gather every session across every job, then hand it to the writer.
   *
   * The sessions are not on this page until a job is expanded, so they are
   * fetched here - in parallel, because a contractor with a dozen jobs should
   * not wait for a dozen round trips in sequence. A job whose time cannot be
   * read is reported rather than silently contributing nothing, since a
   * timesheet that is quietly missing a day is worse than one that fails.
   */
  const gather = async () => {
    const results = await Promise.all(orders.map(async (wo) => {
      try {
        const res = await axios.get(`${API_BASE}/work-orders/${wo.id}/hours`, auth);
        return (Array.isArray(res.data) ? res.data : []).map((h) => ({
          date_worked: h.date_worked,
          hours_logged: Number(h.hours_logged || 0),
          ticket_number: wo.ticket_number || '',
          ticket_title: wo.ticket_title || wo.description || '',
          work_order_number: wo.work_order_number || '',
          description: h.description || '',
          job_logged: Number(wo.logged_hours || 0),
          job_allocated: Number(wo.allocated_hours || 0),
          work_order_status: wo.status || ''
        }));
      } catch {
        return { failed: wo.work_order_number || wo.id };
      }
    }));

    const failed = results.filter((r) => !Array.isArray(r)).map((r) => r.failed);
    const rows = results.filter(Array.isArray).flat();
    rows.sort((a, b) => String(b.date_worked).localeCompare(String(a.date_worked)));
    return { rows, failed };
  };

  const runExport = async (writer, sheet) => {
    setExporting(true);
    setError(null);
    try {
      const { rows, failed } = await gather();
      if (failed.length > 0) {
        setError(`Could not read the time on ${failed.join(', ')} - `
          + 'that job is missing from the download.');
      }
      if (rows.length === 0 && failed.length === 0) {
        setError('There is no time logged yet, so there is nothing to download.');
        return;
      }
      writer(TIME_COLUMNS, rows, 'timesheet', sheet);
    } catch (err) {
      setError('Could not build the download.');
    } finally {
      setExporting(false);
    }
  };

  const openOrder = (wo) => {
    setOpenId(openId === wo.id ? null : wo.id);
    setError(null);
  };

  const when = (v) => (v ? new Date(v).getTime() : 0);
  const SORTS = {
    newest: (a, b) => when(b.created_at) - when(a.created_at),
    oldest: (a, b) => when(a.created_at) - when(b.created_at),
    hours: (a, b) => Number(b.logged_hours || 0) - Number(a.logged_hours || 0),
    // Over budget first, then by how far over - the jobs that need a word
    // with the office before the next session is logged.
    over: (a, b) => {
      const gap = (w) => Number(w.logged_hours || 0) - Number(w.allocated_hours || 0);
      return gap(b) - gap(a);
    },
    status: (a, b) => String(a.ticket_status || '').localeCompare(String(b.ticket_status || ''))
      || when(b.created_at) - when(a.created_at)
  };
  const needle = query.trim().toLowerCase();
  const shown = orders
    .filter((wo) => statusFilter === 'all' || wo.ticket_status === statusFilter)
    .filter((wo) => !needle
      || [wo.work_order_number, wo.ticket_number, wo.ticket_title, wo.description]
        .some((v) => String(v || '').toLowerCase().includes(needle)))
    .sort(SORTS[sortBy] || SORTS.newest);
  // Only the statuses actually on these jobs, so the filter never offers an
  // option that empties the list.
  const statusesHere = [...new Set(orders.map((wo) => wo.ticket_status).filter(Boolean))];

  if (loading) return <p className="no-data">Loading your work&hellip;</p>;

  return (
    <div className="cwo">
      {error && <p className="cwo-error" role="alert">{error}</p>}

      {orders.length > 0 && (
        <div className="cwo-actions">
          <button type="button" className="cwo-btn" disabled={exporting}
                  onClick={() => runExport(downloadCsv)}>
            {exporting ? 'Preparing\u2026' : 'Download CSV'}
          </button>
          <button type="button" className="cwo-btn" disabled={exporting}
                  onClick={() => runExport(downloadXlsx, 'Timesheet')}>
            {exporting ? 'Preparing\u2026' : 'Download Excel'}
          </button>
        </div>
      )}

      {orders.length > 0 && (
        <ListTools
          sortBy={sortBy} onSort={setSortBy}
          sorts={[
            { key: 'newest', label: 'Newest first' },
            { key: 'oldest', label: 'Oldest first' },
            { key: 'hours', label: 'Most hours logged' },
            { key: 'over', label: 'Over budget first' },
            { key: 'status', label: 'Ticket status' }
          ]}
          status={statusFilter} onStatus={setStatusFilter} statuses={statusesHere}
          query={query} onQuery={setQuery} placeholder="Work order, ticket or job"
          onClear={() => { setStatusFilter('all'); setQuery(''); }}
        />
      )}

      {orders.length === 0 ? (
        <p className="no-data">
          No work orders are assigned to you yet. They appear here once one is
          issued, and you can log your time against each as you go.
        </p>
      ) : shown.length === 0 ? (
        <p className="no-data">
          Nothing matches these filters. {orders.length} job{orders.length === 1 ? '' : 's'} in all.
        </p>
      ) : (
        <ul className="cwo-list">
          {shown.map((wo) => {
            const isOpen = openId === wo.id;
            const logged = Number(wo.logged_hours || 0);
            const allocated = Number(wo.allocated_hours || 0);
            const pct = allocated > 0 ? Math.min((logged / allocated) * 100, 100) : 0;
            const over = allocated > 0 && logged > allocated;
            return (
              <li key={wo.id} className="cwo-item">
                <button type="button" className="cwo-head" onClick={() => openOrder(wo)}
                        aria-expanded={isOpen}>
                  <span className="cwo-title">
                    <span className="cwo-title-row">
                      <strong>{wo.work_order_number || 'Work order'}</strong>
                      {/* The ticket's status, highlighted. A job used to show
                          hours against a budget and nothing about whether the
                          ticket was open, on hold or resolved. */}
                      <StatusBadge status={wo.ticket_status} />
                      {wo.status && wo.status !== 'in_progress' && (
                        <span className={`cwo-wo-status is-${wo.status}`}>
                          {String(wo.status).replace(/_/g, ' ')}
                        </span>
                      )}
                    </span>
                    <span className="cwo-sub">
                      {wo.ticket_number ? `${wo.ticket_number} · ` : ''}{wo.ticket_title || wo.description || ''}
                    </span>
                  </span>
                  <span className="cwo-figures">
                    <span className={over ? 'cwo-hours is-over' : 'cwo-hours'}>
                      {hrs(logged)}{allocated > 0 ? ` / ${hrs(allocated)}` : ''}
                    </span>
                    <span className="cwo-track">
                      <span className={over ? 'cwo-fill is-over' : 'cwo-fill'} style={{ width: `${pct}%` }} />
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div className="cwo-body">
                    {/* The ticket this job is against. The header shows its
                        number, but the header is a button - the fold toggle -
                        and a link cannot sit inside one, so the way through to
                        the ticket is here, where the job is actually worked.
                        Without it a contractor could see the number and had no
                        way to read what the fault was. */}
                    {wo.ticket_id && (
                      <p className="cwo-ticket-link">
                        <TicketLink id={wo.ticket_id}>
                          Open {wo.ticket_number || 'the ticket'}
                        </TicketLink>
                      </p>
                    )}
                    {/* The same component, and the same rows, as the ticket in
                        "My tickets". A session added there appears here and
                        the other way round, because there is only one set of
                        rows and one place that writes them. */}
                    <TimeSheet
                      ticketId={wo.ticket_id || undefined}
                      workOrderId={wo.ticket_id ? undefined : wo.id}
                      token={token}
                      loggedHours={logged}
                      allocatedHours={allocated}
                      onChanged={load}
                      emptyNote="No time logged against this job yet. Add your first session below."
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
